// Platform-owner spend intel: what AI scoring of writing and speaking tasks is
// costing, this month against the cap, per task type (with the per-task
// average), and cumulative per organization, split into text grading
// (writing / SWT / SST) and audio grading (speaking). Drill into any org to see
// per-student and per-task cost. This is the owner's cost-control view.
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronDown, Mic, PenLine } from 'lucide-react';

import { api } from '@/lib/api';
import AppShell from '@/components/AppShell';
import { cn } from '@/lib/utils';

// Costs are tiny; show enough precision to be meaningful without noise.
function money(n) {
  const v = Number(n) || 0;
  if (v === 0) return '$0';
  if (v < 0.01) return `$${v.toFixed(5)}`;
  if (v < 1) return `$${v.toFixed(4)}`;
  return `$${v.toFixed(2)}`;
}

function taskLabel(exam, taskType) {
  if (!taskType) return '-';
  return `${(exam || '').toUpperCase()} · ${taskType.replace(/_/g, ' ')}`;
}

function Kpi({ label, value, sub }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-5 py-4">
      <p className="mono-stat">{label}</p>
      <p className="h-display mt-1 text-3xl tabular-nums text-slate-900">{value}</p>
      {sub && <p className="mt-1 text-xs text-slate-400">{sub}</p>}
    </div>
  );
}

// Per-org expandable row: cost by student and by task within the org.
function OrgDrill({ orgId }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    api.adminSpendOrg(orgId).then((r) => setData(r.data)).catch((e) => setErr(e.response?.data?.detail || 'Could not load.'));
  }, [orgId]);
  if (err) return <p className="px-5 py-4 text-sm text-rose-600">{err}</p>;
  if (!data) return <p className="px-5 py-4 text-sm text-slate-400">Loading…</p>;
  return (
    <div className="grid gap-6 bg-slate-50 px-5 py-5 lg:grid-cols-2">
      <div>
        <p className="mono-stat mb-2">by student</p>
        {data.by_student.length === 0 ? (
          <p className="text-sm text-slate-400">No scoring cost from this org yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-200 text-left">
              {['Student', 'Writing', 'Speaking', 'Total'].map((h) => <th key={h} className="px-2 py-1.5"><span className="mono-stat">{h}</span></th>)}
            </tr></thead>
            <tbody>
              {data.by_student.map((s) => (
                <tr key={s.user_id} className="border-b border-slate-100 last:border-0">
                  <td className="px-2 py-1.5"><p className="font-medium text-slate-800">{s.name}</p><p className="text-xs text-slate-400">{s.email}</p></td>
                  <td className="px-2 py-1.5 tabular-nums text-slate-600">{money(s.text_usd)}</td>
                  <td className="px-2 py-1.5 tabular-nums text-slate-600">{money(s.audio_usd)}</td>
                  <td className="px-2 py-1.5 tabular-nums font-semibold text-slate-900">{money(s.total_usd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div>
        <p className="mono-stat mb-2">by task type</p>
        {data.by_task.length === 0 ? (
          <p className="text-sm text-slate-400">-</p>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-200 text-left">
              {['Task', 'Calls', 'Avg', 'Total'].map((h) => <th key={h} className="px-2 py-1.5"><span className="mono-stat">{h}</span></th>)}
            </tr></thead>
            <tbody>
              {data.by_task.map((t, i) => (
                <tr key={i} className="border-b border-slate-100 last:border-0">
                  <td className="px-2 py-1.5 text-slate-700">{taskLabel(t.exam, t.task_type)}</td>
                  <td className="px-2 py-1.5 tabular-nums text-slate-500">{t.calls}</td>
                  <td className="px-2 py-1.5 tabular-nums text-slate-500">{money(t.avg_usd)}</td>
                  <td className="px-2 py-1.5 tabular-nums font-semibold text-slate-900">{money(t.total_usd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default function AdminSpend() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [openOrg, setOpenOrg] = useState(null);

  const load = useCallback(async () => {
    try { setData((await api.adminSpend()).data); }
    catch (err) { setError(err.response?.data?.detail || 'Failed to load spend.'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (error) return <AppShell><p className="border-l-2 border-red-500 bg-red-50/60 px-4 py-3 text-sm text-red-900">{error}</p></AppShell>;
  if (!data) return <AppShell><p className="text-slate-400">Loading…</p></AppShell>;

  const text = data.by_kind?.text_grade || { total_usd: 0, calls: 0 };
  const audio = data.by_kind?.audio_grade || { total_usd: 0, calls: 0 };

  return (
    <AppShell>
      <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900" data-testid="spend-back">
        <ArrowLeft className="h-4 w-4" /> Organizations
      </Link>
      <p className="mono-stat mt-4">platform admin · cost control</p>
      <h1 className="h-display mt-2 text-3xl">AI scoring spend</h1>
      <p className="mt-2 max-w-2xl text-[15px] leading-relaxed text-slate-500">
        What grading writing and speaking responses costs in API spend, the average cost per task,
        and the cumulative cost per organization — each center measured against its own monthly cap.
        Click an org to see per-student and per-task cost.
      </p>

      {/* KPIs */}
      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="spend-kpis">
        <Kpi label="this month" value={money(data.month_total_usd)} sub={`${data.month_calls} grading calls · all centers`} />
        <Kpi label="all time" value={money(data.all_time_total_usd)} sub={`${data.all_time_calls} grading calls`} />
        <Kpi label="writing / text" value={money(text.total_usd)} sub={`${text.calls} calls`} />
        <Kpi label="speaking / audio" value={money(audio.total_usd)} sub={`${audio.calls} calls`} />
      </div>

      {/* Per-center cap explainer (the cap is PER center, not platform-wide) */}
      <div className="mt-4 rounded-lg border border-slate-200 bg-white px-5 py-4" data-testid="per-center-cap-note">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium text-slate-700">Per-center monthly cap</span>
          <span className="tabular-nums text-slate-500">${data.per_center_cap_usd ?? data.cap_usd}/mo default · per organization</span>
        </div>
        <p className="mt-1.5 text-xs text-slate-400">
          Each center has its own monthly grading-cost ceiling, applied independently — one busy center
          can never delay grading for another. When a center reaches its cap, only that center's new
          submissions defer and re-run once the month rolls over or its cap is raised. No student is ever errored.
          Per-center usage vs cap is shown in the table below.
        </p>
      </div>

      {/* By task type */}
      <section className="mt-10">
        <h2 className="font-display text-lg font-semibold text-slate-900">Cost per task type</h2>
        <p className="mt-1 text-sm text-slate-500">Average is the per-call cost; total is cumulative across all organizations.</p>
        {data.by_task.length === 0 ? (
          <p className="mt-4 text-sm text-slate-400">No scoring cost recorded yet. Rows appear once writing/speaking responses are graded.</p>
        ) : (
          <div className="mt-3 overflow-hidden rounded-lg border border-slate-200 bg-white">
            <table className="w-full text-sm" data-testid="spend-by-task">
              <thead><tr className="border-b border-slate-200 text-left">
                {['Task', 'Grading', 'Calls', 'Avg / call', 'Total'].map((h) => <th key={h} className="px-4 py-3"><span className="mono-stat">{h}</span></th>)}
              </tr></thead>
              <tbody>
                {data.by_task.map((t, i) => (
                  <tr key={i} className="border-b border-slate-100 last:border-0">
                    <td className="px-4 py-3 text-slate-800">{taskLabel(t.exam, t.task_type)}</td>
                    <td className="px-4 py-3">
                      <span className={cn('inline-flex items-center gap-1.5 text-xs font-medium', t.kind === 'audio_grade' ? 'text-indigo-600' : 'text-slate-500')}>
                        {t.kind === 'audio_grade' ? <Mic className="h-3.5 w-3.5" /> : <PenLine className="h-3.5 w-3.5" />}
                        {t.kind === 'audio_grade' ? 'audio' : 'text'}
                      </span>
                    </td>
                    <td className="px-4 py-3 tabular-nums text-slate-500">{t.calls}</td>
                    <td className="px-4 py-3 tabular-nums text-slate-500">{money(t.avg_usd)}</td>
                    <td className="px-4 py-3 tabular-nums font-semibold text-slate-900">{money(t.total_usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* By organization */}
      <section className="mt-10">
        <h2 className="font-display text-lg font-semibold text-slate-900">Cost per organization</h2>
        <p className="mt-1 text-sm text-slate-500">Cumulative and this month, split into writing (text) and speaking (audio). Click a row for the per-student breakdown.</p>
        {data.by_org.length === 0 ? (
          <p className="mt-4 text-sm text-slate-400">No org has incurred scoring cost yet.</p>
        ) : (
          <div className="mt-3 overflow-hidden rounded-lg border border-slate-200 bg-white">
            <table className="w-full text-sm" data-testid="spend-by-org">
              <thead><tr className="border-b border-slate-200 text-left">
                {['', 'Organization', 'Students', 'Writing', 'Speaking', 'This month / cap', 'Cumulative'].map((h, i) => <th key={i} className="px-4 py-3"><span className="mono-stat">{h}</span></th>)}
              </tr></thead>
              <tbody>
                {data.by_org.map((o) => {
                  const open = openOrg === o.org_id;
                  return (
                    <React.Fragment key={o.org_id || 'none'}>
                      <tr className="cursor-pointer border-b border-slate-100 hover:bg-slate-50 last:border-0" onClick={() => setOpenOrg(open ? null : o.org_id)} data-testid={`spend-org-${o.org_id}`}>
                        <td className="px-4 py-3"><ChevronDown className={cn('h-4 w-4 text-slate-400 transition-transform', open && 'rotate-180')} /></td>
                        <td className="px-4 py-3 font-medium text-slate-900">{o.org_name}</td>
                        <td className="px-4 py-3 tabular-nums text-slate-500">{o.students}</td>
                        <td className="px-4 py-3 tabular-nums text-slate-600">{money(o.text_usd)}</td>
                        <td className="px-4 py-3 tabular-nums text-slate-600">{money(o.audio_usd)}</td>
                        <td className="px-4 py-3" data-testid={`spend-org-cap-${o.org_id}`}>
                          {(() => {
                            const cap = Number(o.cap_usd) || 0;
                            const pct = cap ? Math.min(100, (o.month_usd / cap) * 100) : 0;
                            return (
                              <div className="min-w-[120px]">
                                <div className="flex items-center justify-between text-xs tabular-nums text-slate-600">
                                  <span>{money(o.month_usd)}</span>
                                  <span className="text-slate-400">/ ${cap}</span>
                                </div>
                                <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                                  <div className={cn('h-full rounded-full', pct >= 90 ? 'bg-rose-500' : pct >= 60 ? 'bg-amber-400' : 'bg-emerald-500')} style={{ width: `${pct}%` }} />
                                </div>
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-3 tabular-nums font-semibold text-slate-900">{money(o.total_usd)}</td>
                      </tr>
                      {open && o.org_id && (
                        <tr><td colSpan={7} className="p-0"><OrgDrill orgId={o.org_id} /></td></tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </AppShell>
  );
}
